import { sb } from '../supabase';

// Admin side of the request fee (299 SAR). Every status change is an RPC —
// the table has no client write policy — and each RPC records who did it:
//   admin_waive_request_fee           awaiting_payment → waived      (reason)
//   admin_flag_request_fee_refund_due paid → refund_due              (reason; the
//                                     daily cron does this for a missed deadline)
//   admin_mark_request_fee_refunded   refund_due → refunded          (the Telr
//                                     refund reference — refund in Telr FIRST)
// Nothing here moves money. A refund is made by hand in Telr's merchant panel,
// then recorded.

const FEE_COLS = 'id, request_id, buyer_id, amount, currency, status, telr_ref, paid_at, refund_deadline_at, '
  + 'credited_at, credited_amount, refund_due_at, refund_due_reason, refund_due_by, refunded_at, refunded_by, '
  + 'refund_reference, refund_reason, waived_at, waive_reason, created_at';

// The fee queue, newest first, with the request and trader attached.
export async function fetchFeeQueue() {
  const { data, error } = await sb.from('managed_request_fees')
    .select(`${FEE_COLS}, request:requests(id, request_ref, title_ar, title_en, status, request_kind, idea_mode, `
      + 'requester:profiles!requests_buyer_id_fkey(full_name, company_name, whatsapp))')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[adminRequestFees] queue failed:', error.code || '', error.message);
    throw error;
  }
  return data || [];
}

export async function fetchFeeForRequest(requestId) {
  const { data, error } = await sb.from('managed_request_fees').select(FEE_COLS).eq('request_id', requestId).maybeSingle();
  if (error) {
    console.error('[adminRequestFees] fee lookup failed for', requestId, ':', error.code || '', error.message);
    throw error;
  }
  return data || null;
}

async function rpc(fn, args) {
  const { data, error } = await sb.rpc(fn, args);
  if (error) {
    console.error(`[adminRequestFees] ${fn} failed:`, error.code || '', error.message);
    throw error;
  }
  return data;
}

export const waiveRequestFee = (requestId, reason) =>
  rpc('admin_waive_request_fee', { p_request_id: requestId, p_reason: reason });

export const flagRequestFeeRefundDue = (requestId, reason) =>
  rpc('admin_flag_request_fee_refund_due', { p_request_id: requestId, p_reason: reason });

export const markRequestFeeRefunded = (requestId, refundReference, note) =>
  rpc('admin_mark_request_fee_refunded', { p_request_id: requestId, p_refund_reference: refundReference, p_note: note || null });

// Admin labels (the console is Arabic/English).
export function adminFeeStatusLabel(status, isAr) {
  const m = {
    awaiting_payment: ['بانتظار دفع التاجر', 'Awaiting trader payment'],
    paid: ['مدفوعة', 'Paid'],
    credited: ['خُصمت من الطلب', 'Credited to order'],
    refund_due: ['مستحقة الاسترداد', 'Refund due'],
    refunded: ['مُستردة', 'Refunded'],
    waived: ['مُعفاة', 'Waived'],
  }[status];
  return m ? (isAr ? m[0] : m[1]) : String(status || '');
}

export function adminFeeBadgeTone(status) {
  return { awaiting_payment: 'warn', paid: 'info', credited: 'ok', refund_due: 'danger', refunded: 'neutral', waived: 'neutral' }[status] || 'neutral';
}

// Days (ceil) until the refund deadline — negative once passed. null if none.
export function daysToDeadline(fee, now = Date.now()) {
  if (!fee?.refund_deadline_at) return null;
  return Math.ceil((new Date(fee.refund_deadline_at).getTime() - now) / 86400000);
}
